import { HardDrive } from "lucide-react";
import {
  PageHeading, Section, Steps, Callout, RoleBadge,
  Diagram, MockButton, MockRow,
} from "../shared";

export default function SettingsBackupSection() {
  return (
    <div data-testid="guide-settings-backup">
      <PageHeading
        icon={HardDrive}
        title="Backup & Restore"
        description="Save a complete copy of everything in the system to a single file, put an earlier copy back, or download an install package for an offline / on-site computer."
      />

      <div className="flex items-center gap-2 mb-4">
        <RoleBadge role="it_admin" />
        <span className="text-xs text-white/40">Needs the <span className="font-mono text-white/70">manage_backup</span> permission. Restoring is destructive, so it asks for your password first.</span>
      </div>

      <Callout variant="info" title="In plain words">
        A backup is one big file that holds everything — all the action items, people, history and settings. You download it to keep it safe. If something ever goes badly wrong, you can restore it and the system goes back to exactly how it was when you took that backup.
      </Callout>

      <Section title="When do I use this?">
        <ul className="list-disc list-inside text-xs text-white/65 space-y-1">
          <li>Regularly, to keep a safe copy off the machine.</li>
          <li>Before a big change, so you can undo it if needed.</li>
          <li>To move the system to a new computer, or set up an offline install.</li>
        </ul>
      </Section>

      <Section title="The screen">
        <Diagram
          frameLabel="Settings → Backup & Restore"
          legend={[
            <>Press <span className="text-white">Download Backup</span> to save everything as one file.</>,
            <>Press <span className="text-white">Restore from Backup</span> to put a saved file back (this replaces everything).</>,
            <>Press <span className="text-white">Download Compiled Package</span> to get an install kit for an offline computer.</>,
          ]}
        >
          <MockRow>
            <MockButton label="Download Backup" marker={1} />
            <MockButton label="Restore from Backup" marker={2} variant="danger" />
          </MockRow>
          <MockButton label="Download Compiled Package" marker={3} variant="ghost" />
        </Diagram>
      </Section>

      <Section title="How to download a backup">
        <Steps steps={[
          <>Press <span className="text-white">Download Backup</span>. The system gives you one file containing every part of the database (action items, history, sub-tasks, people, the role/permission settings, email settings, branding, performance settings, logs, and more).</>,
          <>Save the file somewhere safe — ideally encrypted and off this machine. It contains email addresses, password data, and mailbox credentials, so treat it like a vault key.</>,
        ]} />
      </Section>

      <Section title="How to restore a backup">
        <Steps steps={[
          <>Press <span className="text-white">Restore from Backup</span> and choose the saved file.</>,
          <>Confirm with your password. The restore <span className="text-white">replaces everything</span> — it does not merge. Whatever is in the file overwrites what's there now.</>,
          <>Your own session keeps working. Other people may need to sign in again if their sessions weren't part of the file.</>,
        ]} />
      </Section>

      <Section title="Download Compiled Package">
        <p className="text-sm text-white/65 leading-relaxed">
          This makes a self-contained zip with the whole app built and ready, plus a short README with the Windows install steps. Use it to deliver the system to a customer site with no internet, or to set up a fresh on-site copy.
        </p>
      </Section>

      <Callout variant="danger" title="Restore erases the current data">
        There is no merge option. If you might want today's data back later, take a fresh backup of the <em>current</em> state before you restore an older one.
      </Callout>
      <Callout variant="warning" title="Backups contain secrets">
        Mailbox credentials and password data are inside the file. Don't email it, don't put it in shared code, and don't leave it on a shared drive.
      </Callout>
    </div>
  );
}
